import React, { useState, useEffect, useMemo, useLayoutEffect } from 'react';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Button } from '@progress/kendo-react-buttons';
import {
  Users, UserCheck, UserX, UserPlus, Search, LayoutGrid, List,
  Mail, Phone, Shield, Edit2, Trash2, Power, Circle, MoreVertical, Filter,
  AtSign, Check, AlertCircle, Key, Eye, EyeOff, Copy,
} from 'lucide-react';
import { portalUsers } from '../../data/mockData';
import { userApi } from '../../api/userApi';
import { isApiConfigured } from '../../api/axiosClient';
import { LoadingSpinner, ErrorCard } from '../../components/LoadingSpinner';
import { useToast } from '../../components/Toast';
import { CardsPagination } from '../../components/CardsPagination';
import { useConfirm } from '../../components/ConfirmDialog';
import { ValidatedInput } from '../../components/ValidatedInput';
import { validators } from '../../utils/validators';
import { useAuth } from '../../auth/AuthContext';
import { process } from "@progress/kendo-data-query";
import { formatRelative } from '../../utils/loginTracker';


/* ── Role → colour ──────────────────────────────────────── */
const ROLE_COLORS = {
  'Super Admin': { bg: '#FEE2E2', color: '#DC2626', ring: 'rgba(220,38,38,0.15)' },
  Admin:         { bg: '#EDE9FE', color: '#7C3AED', ring: 'rgba(124,58,237,0.15)' },
  Provider:      { bg: '#DBEAFE', color: '#2563EB', ring: 'rgba(37,99,235,0.15)' },
  Staff:         { bg: '#D1FAE5', color: '#059669', ring: 'rgba(5,150,105,0.15)' },
  Viewer:        { bg: '#FEF3C7', color: '#D97706', ring: 'rgba(217,119,6,0.15)' },
};

const emptyUser = {
  userName: '',
  firstName: '', lastName: '',
  email: '', phone: '',
  password: '', confirmPassword: '',
  role: '', status: 'Active',
};

/* ── Username validation ────────────────────────────────
 * Rules:
 *   - required
 *   - 3-30 chars
 *   - lowercase letters, digits, and .  _  - only
 *   - must start with a letter
 *   - uniqueness checked against existing users (case-insensitive),
 *     excluding the currently-edited user (by id) so that editing
 *     without changing the username is allowed.
 *
 * Returns { valid: boolean, reason: string | null }
 */
const USERNAME_PATTERN = /^[a-z][a-z0-9._-]{2,29}$/;
const EMAIL_PATTERN    = /^[a-z0-9][a-z0-9._+-]*@[a-z0-9][a-z0-9.-]*\.[a-z]{2,}$/;

/**
 * A username may be either:
 *   (a) a traditional handle  — 3-30 chars, starts with a letter,
 *       lowercase letters / digits / . _ - only
 *   (b) a full email address  — e.g. jane@clinic.com
 * In both cases the value must be unique across users (case-insensitive).
 */
const validateUsername = (raw, allUsers, currentUserId) => {
  const username = (raw || '').trim().toLowerCase();
  if (!username)             return { valid: false, reason: 'Username is required.' };
  if (username.length < 3)   return { valid: false, reason: 'Must be at least 3 characters.' };
  if (username.length > 254) return { valid: false, reason: 'Must be 254 characters or fewer.' };

  const isEmail = username.includes('@');
  if (isEmail) {
    if (!EMAIL_PATTERN.test(username)) {
      return { valid: false, reason: 'Enter a valid email address (e.g. jane@clinic.com).' };
    }
  } else {
    if (username.length > 30) {
      return { valid: false, reason: 'Username must be 30 characters or fewer (or use a full email).' };
    }
    if (!USERNAME_PATTERN.test(username)) {
      return {
        valid: false,
        reason: 'Use lowercase letters, digits, and . _ - only. Must start with a letter — or enter a full email address.',
      };
    }
  }

  const clash = allUsers.some(
    (u) => u.id !== currentUserId && (u.userName || '').toLowerCase() === username
  );
  //if (clash) return { valid: false, reason: 'This username is already taken. Please choose another.' };
  return { valid: true, reason: null };
};

/**
 * Cryptographically-secure password generator.
 * Produces a 14-character password guaranteed to contain at least one
 * uppercase letter, one lowercase letter, one digit, and one symbol.
 * Excludes visually-ambiguous characters (I, l, 1, O, 0) to reduce
 * copy/paste errors when sharing the password with a new user.
 */
const generatePassword = (length = 14) => {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghjkmnpqrstuvwxyz';
  const digits = '23456789';
  const symbols = '!@#$%^&*-_+=?';
  const all = upper + lower + digits + symbols;

  const randomInt = (max) => {
    const buf = new Uint32Array(1);
    (window.crypto || window.msCrypto).getRandomValues(buf);
    return buf[0] % max;
  };

  // Guarantee at least one char from each category.
  const chars = [
    upper[randomInt(upper.length)],
    lower[randomInt(lower.length)],
    digits[randomInt(digits.length)],
    symbols[randomInt(symbols.length)],
  ];
  while (chars.length < length) {
    chars.push(all[randomInt(all.length)]);
  }
  // Fisher-Yates shuffle so the guaranteed chars aren't always at the start.
  for (let i = chars.length - 1; i > 0; i--) {
    const j = randomInt(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
};

const suggestUsername = (firstName = '', lastName = '', takenSet = new Set()) => {
  const clean = (s) => s.replace(/^Dr\.\s*/i, '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const first = clean(firstName);
  const last = clean(lastName);
  if (!first && !last) return '';
  const base = (first.charAt(0) + last).slice(0, 20) || first.slice(0, 20);
  let candidate = base;
  let i = 1;
  while (takenSet.has(candidate)) {
    candidate = `${base}${i}`;
    i += 1;
    if (i > 99) break; // safety
  }
  return candidate;
};

/* ── Avatar component ───────────────────────────────────── */
const Avatar = ({ firstName = '', lastName = '', role, size = 44 }) => {
  const initials = `${firstName.replace(/^Dr\.\s*/, '').charAt(0) || ''}${lastName.charAt(0) || ''}`.toUpperCase() || '?';
  const rc = ROLE_COLORS[role] || { bg: '#F3F4F6', color: '#6B7280' };
  return (
    <div
      style={{
        width: size, height: size, borderRadius: size * 0.32,
        background: `linear-gradient(135deg, ${rc.color}22, ${rc.color}11)`,
        color: rc.color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: size * 0.36, fontWeight: 700,
        border: `1.5px solid ${rc.ring || rc.bg}`,
        flexShrink: 0,
        letterSpacing: 0.3,
      }}
    >
      {initials}
    </div>
  );
};

/* ── Status pill ────────────────────────────────────────── */
const StatusPill = ({ status, onClick }) => {
  const active = status === 'Active';
  return (
    <span
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '4px 10px 4px 8px', borderRadius: 999,
        fontSize: 12, fontWeight: 700,
        background: active ? 'rgba(16,185,129,0.1)' : 'rgba(156,163,175,0.12)',
        color: active ? '#059669' : '#6B7280',
        border: `1px solid ${active ? 'rgba(16,185,129,0.25)' : 'rgba(156,163,175,0.25)'}`,
        cursor: onClick ? 'pointer' : 'default',
        userSelect: 'none',
      }}
      title={onClick ? 'Click to toggle status' : undefined}
    >
      <Circle
        size={8}
        strokeWidth={0}
        fill={active ? '#10B981' : '#9CA3AF'}
      />
      {status}
    </span>
  );
};

/* ── Role badge ─────────────────────────────────────────── */
const RoleBadge = ({ role }) => {
  const rc = ROLE_COLORS[role] || { bg: '#F3F4F6', color: '#6B7280' };
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '3px 10px', borderRadius: 999,
        fontSize: 11, fontWeight: 700,
        background: rc.bg, color: rc.color,
        letterSpacing: 0.3,
      }}
    >
      {role}
    </span>
  );
};

/* ── User Card (for card grid view) ─────────────────────── */
const UserCard = ({ user, onEdit, onDelete, onToggleStatus, onPasswordChange }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const active = user.status === 'Active';

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #E8ECF0',
        borderRadius: 16,
        padding: 18,
        position: 'relative',
        transition: 'all 0.2s',
        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
        opacity: active ? 1 : 0.75,
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = '#CBD5E1';
        e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.06)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = '#E8ECF0';
        e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.03)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      {/* Header: avatar + name + menu */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 14 }}>
        <Avatar firstName={user.firstName} lastName={user.lastName} role={user.role} size={48} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 15, fontWeight: 700, color: '#1A1D21',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {user.firstName} {user.lastName}
          </div>
          {user.userName && (
            <div style={{
              fontSize: 12, color: '#6B7280', fontFamily: 'monospace',
              marginTop: 1, display: 'flex', alignItems: 'center', gap: 3,
            }}>
              <AtSign size={11} strokeWidth={2.5} />{user.userName}
            </div>
          )}
          <div style={{ marginTop: 4, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <RoleBadge role={user.role} />
            <StatusPill name="statusPillCard" status={user.status} onClick={() => onToggleStatus(user.id)} />
          </div>
        </div>

        {/* Kebab menu */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            style={{
              width: 32, height: 32, borderRadius: 8, border: 'none',
              background: menuOpen ? '#F3F4F6' : 'transparent',
              cursor: 'pointer', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              color: '#6B7280',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#F3F4F6'; }}
            onMouseLeave={e => { if (!menuOpen) e.currentTarget.style.background = 'transparent'; }}
            aria-label="More options"
          >
            <MoreVertical size={16} strokeWidth={2} />
          </button>
          {menuOpen && (
            <>
              <div style={{ position: 'fixed', inset: 0, zIndex: 10 }} onClick={() => setMenuOpen(false)} />
              <div style={{
                position: 'absolute', right: 0, top: 36, zIndex: 11,
                background: '#fff', border: '1px solid #E8ECF0',
                borderRadius: 12, boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                minWidth: 180, padding: 6, overflow: 'hidden',
              }}>
                <MenuItem
                  icon={Key}
                  label="Change password"
                  color="#7C3AED"
                  onClick={() => { setMenuOpen(false); onPasswordChange(user); }}
                />
                <MenuItem icon={Edit2} label="Edit user" onClick={() => { setMenuOpen(false); onEdit(user); }} />
                <MenuItem
                  icon={Power}
                  label={active ? 'Deactivate' : 'Activate'}
                  color={active ? '#D97706' : '#059669'}
                  onClick={() => { setMenuOpen(false); onToggleStatus(user.id); }}
                />
                <div style={{ height: 1, background: '#F1F5F9', margin: '4px 0' }} />
                <MenuItem
                  icon={Trash2}
                  label="Delete user"
                  color="#DC2626"
                  onClick={() => { setMenuOpen(false); onDelete(user.id); }}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Body: contact info */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
        <div style={contactRow}>
          <Mail size={14} color="#9CA3AF" strokeWidth={2} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {user.email || '—'}
          </span>
        </div>
        <div style={contactRow}>
          <Phone size={14} color="#9CA3AF" strokeWidth={2} />
          <span>{user.phone || '—'}</span>
        </div>
          </div>
          {/*disabled note*/}
          <div style={{ fontSize: 11, color: '#9CA3AF' }}>

              {user.isDisabledDueToInactivity && (
                  <strong style={{ color: '#EF4444' }}>
                      User Disabled (Inactive) due to prolonged inactivity.
                  </strong>
              )}
          </div>  

      {/* Footer: last login + primary actions */}
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', paddingTop: 12,
        borderTop: '1px solid #F1F5F9',
      }}>
        <div style={{ fontSize: 11, color: '#9CA3AF' }}>
          {user.lastLogin ? `Last seen ${relativeDate(user.lastLogin)}` : 'Never signed in'}
                  {/*Last seen <strong style={{ color: '#6B7280' }}>{relativeDate(user.lastLogin)}</strong>     */}

                  {/*{*/}
                  {/*    user.lastLogin*/}
                  {/*        ? `Last seen ${formatRelative(user.lastLogin)} ${new Date(*/}
                  {/*            user.lastLogin*/}
                  {/*        ).toLocaleString('en-US', {*/}
                  {/*            month: 'short',*/}
                  {/*            day: 'numeric',*/}
                  {/*            hour: 'numeric',*/}
                  {/*            minute: '2-digit',*/}
                  {/*        })}`*/}
                  {/*        : 'Never signed in'*/}
                  {/*}*/}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            onClick={() => onToggleStatus(user.id)}
            style={{
              padding: '6px 12px', borderRadius: 8,
              border: `1px solid ${active ? 'rgba(217,119,6,0.25)' : 'rgba(5,150,105,0.25)'}`,
              background: active ? 'rgba(245,158,11,0.08)' : 'rgba(16,185,129,0.08)',
              color: active ? '#D97706' : '#059669',
              fontSize: 12, fontWeight: 700, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 5,
              fontFamily: 'inherit',
            }}
          >
            <Power size={12} strokeWidth={2.5} />
            {active ? 'Deactivate' : 'Activate'}
          </button>
          <button
            onClick={() => onEdit(user)}
            style={{
              padding: '6px 12px', borderRadius: 8,
              border: '1px solid rgba(13,148,136,0.2)',
              background: 'rgba(13,148,136,0.08)',
              color: '#0D9488',
              fontSize: 12, fontWeight: 700, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 5,
              fontFamily: 'inherit',
            }}
          >
            <Edit2 size={12} strokeWidth={2.5} />
            Edit
          </button>
        </div>
      </div>
    </div>
  );
};

const MenuItem = ({ icon: Icon, label, onClick, color = '#1A1D21' }) => (
  <button
    onClick={onClick}
    style={{
      display: 'flex', alignItems: 'center', gap: 10,
      width: '100%', padding: '9px 12px', borderRadius: 8,
      border: 'none', background: 'transparent',
      fontSize: 13, fontWeight: 600, color,
      cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
    }}
    onMouseEnter={e => { e.currentTarget.style.background = '#F8FAFB'; }}
    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
  >
    <Icon size={15} strokeWidth={2} />
    {label}
  </button>
);

const contactRow = {
  display: 'flex', alignItems: 'center', gap: 8,
  fontSize: 13, color: '#6B7280',
};

const relativeDate = (iso) => {
  const diff = Date.now() - new Date(iso).getTime();
  const d = Math.floor(diff / 86400000);
  if (d < 1) return 'today';
  if (d < 2) return 'yesterday';
  if (d < 7) return `${d}d ago`;
  if (d < 30) return `${Math.floor(d / 7)}w ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/* ── UsernameField: input with live availability check ───────── */
const UsernameField = ({ value, onChange, allUsers, currentUserId, readOnly }) => {
  const { valid, reason } = validateUsername(value, allUsers, currentUserId);
  const isEmpty = !value || !value.trim();
  // Only show validation state after the user has typed something
  const showValid = !isEmpty && valid;
  const showError = !isEmpty && !valid;

  const borderColor = readOnly
    ? '#E8ECF0'
    : showValid
    ? '#059669'
    : showError
    ? '#DC2626'
    : '#E8ECF0';

  return (
    <>
      <div
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          border: `1.5px solid ${borderColor}`,
          borderRadius: 10,
          background: readOnly ? '#F3F4F6' : '#F8FAFB',
          transition: 'all 0.15s',
        }}
      >
        <AtSign
          size={15}
          color="#9CA3AF"
          strokeWidth={2}
          style={{ position: 'absolute', left: 12 }}
        />
        <input
          type="text"
          value={value}
          readOnly={readOnly}
          onChange={(e) => {
            if (readOnly) return;
            // Allow characters for both a traditional username (a-z 0-9 . _ -)
            // and an email address (adds @ and +). Keep lowercase throughout.
            const cleaned = e.target.value.toLowerCase().replace(/[^a-z0-9._+@-]/g, '');
            onChange(cleaned.slice(0, 254));
          }}
          placeholder="Choose a unique username or email"
          style={{
            flex: 1,
            minWidth: 0,
            padding: '10px 40px 10px 36px',
            border: 'none',
            background: 'transparent',
            fontSize: 14,
            fontFamily: 'inherit',
            outline: 'none',
            color: readOnly ? '#6B7280' : '#1A1D21',
            cursor: readOnly ? 'not-allowed' : 'text',
          }}
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck="false"
        />
        {!readOnly && showValid && (
          <Check
            size={16}
            color="#059669"
            strokeWidth={2.5}
            style={{ position: 'absolute', right: 14 }}
          />
        )}
        {!readOnly && showError && (
          <AlertCircle
            size={16}
            color="#DC2626"
            strokeWidth={2.5}
            style={{ position: 'absolute', right: 14 }}
          />
        )}
      </div>
      {/* Helper / validation text */}
      <div
        style={{
          marginTop: 6,
          fontSize: 12,
          fontWeight: 500,
          color: readOnly
            ? '#9CA3AF'
            : showValid
            ? '#059669'
            : showError
            ? '#DC2626'
            : '#6B7280',
          display: 'flex',
          alignItems: 'center',
          gap: 5,
        }}
      >
              {readOnly
                  ? 'Usernames cannot be changed after account creation.'
                  : isEmpty
                      ? 'Enter a username (letters, digits, . _ -) or an email address.'
                      : showValid
                  //? <>{value} is available</>
                  ? ""
                  : reason}
              {/*}*/}
      </div>
    </>
  );
};

const capitalize = (str) => {
    const value = str?.trim();
    return value
        ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
        : '';
};

/* ──────────────────────────────────────────────────────────────────── */
/* Main component                                                      */
/* ──────────────────────────────────────────────────────────────────── */
const UserManagementPage = () => {
    const { user } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const [users, setUsers] = useState([]);
  // When an admin generates or wants to verify a password, we temporarily
  // flip both password fields from type="password" to type="text".
  // Reset whenever the dialog closes so the next Add-User starts hidden.
  const [revealPassword, setRevealPassword] = useState(false);

  // Admin password-reset dialog state. Separate from the Add/Edit dialog so
  // an admin can reset a specific user's password without seeing/editing
  // anything else about their profile.
  const [passwordChangeUser, setPasswordChangeUser] = useState(null);
  const [pwdNew, setPwdNew] = useState('');
  const [pwdConfirm, setPwdConfirm] = useState('');
  const [pwdReveal, setPwdReveal] = useState(false);
  const [pwdSaving, setPwdSaving] = useState(false);

  // Track viewport width so the Add/Edit dialog can resize responsively
  // (Kendo's Dialog `width` prop is numeric; it won't re-measure on its own).
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [showDialog, setShowDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [filterRole, setFilterRole] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [viewMode, setViewMode] = useState('cards'); // 'cards' | 'table'
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(false);
  const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    const [roles, setRoles] = useState([]);



    const styles = {
        overlay: {
            position: "fixed",
            inset: 0, // replaces top/left/width/height
            backgroundColor: "rgba(255,255,255,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
        },
        gridOverlay: {
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(255,255,255,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000
        }
    };


    const getUsers= async() => {
        userApi.getUsers({ userId: user.userId })
            .then((res) => {

                // Example: customize response
                const formattedUsers = res.data.map(u => ({
                    id: u.Id,
                    userName: u.Username,
                    firstName: capitalize(u.Firstname) || '',
                    lastName: capitalize(u.Lastname) || '',
                    lastLogin: u.LastLogin || '',
                    email: u.Email || '',
                    //phone: u.Attributes?.phone?.[0] || 'N/A',
                    phone: formatters.phone(u.Attributes?.phone?.[0]) || 'N/A',
                    role: u.Attributes?.userRole?.[0] || 'N/A',
                    status: u.Enabled ? "Active" : "Inactive",
                    isDisabledDueToInactivity:
                            u.Attributes?.isDisabledDueToInactivity?.[0]?.trim().toLowerCase() === "true",
                    userFullName: `${u.Firstname || ''} ${u.Lastname || ''}`.trim()
                }));

                //console.log("Formatted Users:", formattedUsers);



                setUsers(formattedUsers);
            })
            .catch((err) => setError(err.message || 'Failed to load users'))
            .finally(() => setLoading(false));
    };

  /* Load from API if configured */
  useEffect(() => {
    if (isApiConfigured()) {
        setLoading(true);
        
        

        const fetchData = async () => {
            await getUsers();
        };


        //get USer roles
        userApi.getUserRoles()
            .then((res) => {                
                setRoles(res.data);                
                emptyUser.role = res?.data[0]?.Name;
            });

        fetchData();
    }
      
  }, []);

    const updateField = (field, value) => {
        setEditingUser(prev => ({ ...prev, [field]: value }));
        setError('');
    };

  /* Derived ─ filtered list & stats */
  const filteredUsers = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return users.filter((u) => {
      if (q && !`${u.firstName} ${u.lastName} ${u.email} ${u.userName || ''}`.toLowerCase().includes(q)) return false;
      if (filterRole !== 'All' && u.role !== filterRole) return false;
      if (filterStatus !== 'All' && u.status !== filterStatus) return false;
      return true;
    });
  }, [users, searchText, filterRole, filterStatus]);

  const stats = useMemo(() => ({
    total: users.length,
    active: users.filter(u => u.status === 'Active').length,
    inactive: users.filter(u => u.status === 'Inactive').length,
    admins: users.filter(u => ['Provider Admin', 'Super Admin'].includes(u.role)).length,
  }), [users]);

  /* Actions */
  const openAddDialog = () => {
    setEditingUser({ ...emptyUser, id: null });
    setRevealPassword(false);
    setShowDialog(true);
  };
  const openEditDialog = (user) => {
    setEditingUser({ ...user });
    setRevealPassword(false);
    setShowDialog(true);
  };
  const closeDialog = () => {
    setShowDialog(false);
    setEditingUser(null);
    setRevealPassword(false);
  };

  /* ── Admin password reset dialog ─────────────────── */
  const openPasswordChangeDialog = (user) => {
    setPasswordChangeUser(user);
    setPwdNew('');
    setPwdConfirm('');
    setPwdReveal(false);
  };
  const closePasswordChangeDialog = () => {
    setPasswordChangeUser(null);
    setPwdNew('');
    setPwdConfirm('');
    setPwdReveal(false);
  };
  const handleSavePasswordChange = async () => {
    if (!passwordChangeUser) return;
    const pwdError = validators.password(pwdNew);
    if (pwdError) {
      toast.error(pwdError, { title: 'Password does not meet requirements' });
      return;
    }
    if (pwdNew !== pwdConfirm) {
      toast.error('Passwords do not match. Please re-enter.', { title: 'Password mismatch' });
      return;
    }
    const displayName = `${passwordChangeUser.firstName} ${passwordChangeUser.lastName}`;
    if (isApiConfigured()) {
      setPwdSaving(true);
        try {
            //alert("Change PAssword hit");
            const res= await userApi.resetPassword({
                userId: passwordChangeUser.id,
                newPassword: pwdNew
            });

            if (res.success) {
                toast.success(
                    `${displayName}'s password has been reset.`,
                    { title: 'Password changed' }
                );
                closePasswordChangeDialog();
            }
            else {
                toast.error(err.message || 'Failed to change password', { title: 'Password change failed' });
            }
        
        
      } catch (err) {
        toast.error(err.message || 'Failed to change password', { title: 'Password change failed' });
      } finally { setPwdSaving(false); }
    } else {
      // Mock mode — don't persist the password anywhere, just acknowledge.
      
      closePasswordChangeDialog();
    }
  };


     const formatters = {
        /* Format phone as (XXX) XXX-XXXX */
        phone: (value) => {
            if (!value) return '';
            const digits = value.replace(/\D/g, '').slice(0, 10);
            if (digits.length < 4) return digits;
            if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
            return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
        }
    };
  const handleSave = async () => {
    if (!editingUser.firstName || !editingUser.lastName || !editingUser.email) {
      toast.error('First Name, Last Name, and Email are required.', { title: 'Missing information' });
      return;
    }
      const uname = validateUsername(editingUser.userName, users, editingUser.id);

     //alert("UNAME : " + JSON.stringify(editingUser));

    if (!uname.valid) {
      toast.error(uname.reason, { title: 'Invalid username' });
      return;
    }
    // Normalise username to lowercase on save
    editingUser.userName = (editingUser.userName || '').trim().toLowerCase();
    const isCreate = !editingUser.id;

    // Password is required only when creating a new user. Existing users
    // change their password through the Profile page, not this admin form.
    if (isCreate) {
      const pwdError = validators.password(editingUser.password || '');
      if (pwdError) {
        toast.error(pwdError, { title: 'Password does not meet requirements' });
        return;
      }
      if ((editingUser.password || '') !== (editingUser.confirmPassword || '')) {
        toast.error('Passwords do not match. Please re-enter.', { title: 'Password mismatch' });
        return;
      }
    }

    // Build the payload. confirmPassword + internal UI flags never leave the
    // client. In mock mode we also strip the password before storing in state
    // so we never persist plaintext credentials client-side.
      const payload = {
          ...editingUser,
          taxId: user.taxId,
          parentUserId:user.userId
      };
    delete payload.confirmPassword;
    delete payload._usernameTouched;

    const displayName = `${editingUser.firstName} ${editingUser.lastName}`;

    if (isApiConfigured()) {
      setSaving(true);
      try {
        if (editingUser.id) {
          // Never send password on an update from this admin form.
          const updatePayload = { ...payload };
            delete updatePayload.password;

            
            
            const res = await userApi.updateUser(updatePayload);
            if (res.success) {
                toast.success(`${displayName} was updated.`, { title: 'User updated' });
                setUsers(users.map(u => u.id === editingUser.id ? editingUser : u));
                setShowDialog(false);
                setEditingUser(null);
            }
            else {
                toast.error(`${displayName} was not updated.`, { title: 'Error in Update' });
            }
            //setUsers(users.map(u => u.id === editingUser.id ? res.data : u));
            
            

          
        }
        else
        {   
            
            const res = await userApi.createUser(payload);

            if (res.success) {
                setUsers([...users, payload]);
                toast.success(`${displayName} was added to the portal.`, { title: 'User created' });
                setShowDialog(false);
                setEditingUser(null);
                //location.reload();
                await getUsers();
            }
            else {
                
                toast.error(res.message, {title:`Error`});
               // setShowDialog(true);
                
            }
          
        }
        
        
      } catch (err) {
        toast.error(err.message || `Failed to ${isCreate ? 'create' : 'update'} user`, {
          title: `Could not ${isCreate ? 'create' : 'update'} user`,
        });
      } finally { setSaving(false); }
    } else {
      // Mock mode — do NOT keep the plaintext password in local state.
      const storedPayload = { ...payload };
      delete storedPayload.password;

      if (editingUser.id) {
        setUsers(users.map(u => u.id === editingUser.id ? storedPayload : u));
        toast.success(`${displayName} was updated.`, { title: 'User updated' });
      } else {
        const newId = Math.max(0, ...users.map(u => u.id)) + 1;
        setUsers([
          ...users,
          { ...storedPayload, id: newId, lastLogin: null, createdAt: new Date().toISOString() },
        ]);
        toast.success(`${displayName} was added to the portal.`, { title: 'User created' });
      }
      setShowDialog(false);
      setEditingUser(null);
    }
  };

    const handleDeleteCard = async (usr) => {

        //alert("handleDeleteCard : "+user.id);
        const u = users.find(x => x.id === usr.id);

        if (!u) return;

        const ok = await confirm({
            title: `Delete ${u.firstName} ${u.lastName}?`,
            message: `This will delete user ${u.firstName} from the portal and revoke their access immediately.`,
            confirmLabel: 'Delete User',
            cancelLabel: 'Cancel',
            variant: 'danger',
        });

        if (!ok) return;

        const displayName = `${u.firstName} ${u.lastName}`;
        if (isApiConfigured()) {
            setPageLoading(true);

            try {

                const res = await userApi.deleteUser({
                    UserId: u.id,

                });
                if (res.success) {
                    setUsers(users.filter(x => x.id !== u.id));
                    toast.success(`${displayName} was removed from the portal.`, { title: 'User deleted' });
                }
                else {
                    toast.error(err.message || 'Failed to delete user', { title: 'Delete failed' });
                }
                setPageLoading(false);

            } catch (err) {
                setPageLoading(false);
                toast.error(err.message || 'Failed to delete user', { title: 'Delete failed' });
            }
        } else {
            setUsers(users.filter(x => x.id !== userId));
            toast.success(`${displayName} was removed from the portal.`, { title: 'User deleted' });
        }
    };

    const handleDelete = async (user) => {

        //alert("DELETE USER : "+JSON.stringify(user));
        const u = users.find(x => x.id === user.dataItem.id);
        
        if (!u) return;
        
    const ok = await confirm({
      title: `Delete ${u.firstName} ${u.lastName}?`,
      message: `This will delete user ${u.firstName} from the portal and revoke their access immediately.`,
      confirmLabel: 'Delete User',
      cancelLabel: 'Cancel',
      variant: 'danger',
    });
        
        if (!ok) return;
        
    const displayName = `${u.firstName} ${u.lastName}`;
    if (isApiConfigured()) {
        try {
            setPageLoading(true);
            
          const res= await userApi.deleteUser({
              UserId: user.dataItem.id,              

          });
          if (res.success) {
              setUsers(users.filter(x => x.id !== user.dataItem.id));
              
              toast.success(`${displayName} was removed from the portal.`, { title: 'User deleted' });
          }
          else {
              toast.error(err.message || 'Failed to delete user', { title: 'Delete failed' });
          }
            setPageLoading(false);
        
        } catch (err) {
            setPageLoading(false);
            toast.error(err.message || 'Failed to delete user', { title: 'Delete failed' });
      }
    } else {
      setUsers(users.filter(x => x.id !== userId));
      toast.success(`${displayName} was removed from the portal.`, { title: 'User deleted' });
    }
  };

    const toggleStatus = async (dataItem) => {        
        
        const u = users.find(x => x.id === dataItem.id);
    if (!u) return;
    const nextStatus = u.status === 'Active' ? 'Inactive' : 'Active';
    const action = nextStatus === 'Active' ? 'activate' : 'deactivate';
    const ok = await confirm({
      title: `${nextStatus === 'Active' ? 'Activate' : 'Deactivate'} ${u.firstName} ${u.lastName}?`,
      message: nextStatus === 'Active'
        ? `${u.firstName} will regain access to the portal.`
        : `${u.firstName} will lose access until they're activated again.`,
      confirmLabel: nextStatus === 'Active' ? 'Activate' : 'Deactivate',
      variant: nextStatus === 'Active' ? 'info' : 'warning',
    });
    if (!ok) return;
        const displayName = `${u.firstName} ${u.lastName}`;

        

        if (isApiConfigured())
        {
          try {
              setPageLoading(true);
              const res = await userApi.toggleStatus({
                  userId: dataItem.id,
                  userStatus: dataItem.status
              });
            //setUsers(users.map(x => x.id === dataItem.id ? res.data : x));
              //setUsers(users.map(x => x.id === dataItem.id));
              //setUsers(users.map(x =>
              //    x.id === dataItem.id ? dataItem : x
              //));

              setUsers(users.map(x => x.id === dataItem.id ? { ...x, status: nextStatus, isDisabledDueToInactivity:false } : x));

              setPageLoading(false);
            toast.success(
              `${displayName} is now ${nextStatus}.`,
              { title: nextStatus === 'Active' ? 'User activated' : 'User deactivated' }
            );
          } catch (err)
          {
              setPageLoading(false);
            toast.error(err.message || 'Failed to toggle user status', { title: 'Status change failed' });
          }
        }
        else
        {
              setUsers(users.map(x => x.id === userId ? { ...x, status: nextStatus } : x));
              toast.success(
                `${displayName} is now ${nextStatus}.`,
                { title: nextStatus === 'Active' ? 'User activated' : 'User deactivated' }
              );
        }
  };

  /* ── Table cell renderers ──────────────────────────────── */
  const NameCell = (props) => (
    <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Avatar
          firstName={props.dataItem.firstName}
          lastName={props.dataItem.lastName}
          role={props.dataItem.role}
          size={32}
        />
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span style={{
            fontWeight: 600, color: '#1A1D21', fontSize: 13,
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            {props.dataItem.firstName} {props.dataItem.lastName}
            {props.dataItem.userName && (
              <span style={{
                fontSize: 11, color: '#6B7280', fontFamily: 'monospace',
                fontWeight: 500,
              }}>
                @{props.dataItem.userName}
              </span>
            )}
          </span>
          <span style={{ fontSize: 11, color: '#9CA3AF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {props.dataItem.email}
          </span>
        </div>
      </div>
    </td>
  );

  const RoleCellT = (props) => (
    <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
      <RoleBadge role={props.dataItem.role} />
    </td>
  );

  const StatusCellT = (props) => (
    <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
      <StatusPill
        status={props.dataItem.status}
        onClick={() => toggleStatus(props.dataItem)}
      />
    </td>
  );

  const LastLoginCellT = (props) => {
    const val = props.dataItem.lastLogin;
    return (
      <td {...props.tdProps} colSpan={props.colSpan} style={{ ...props.style, color: '#6B7280', fontSize: 12 }}>
        {val ? relativeDate(val) : <span style={{ color: '#9CA3AF' }}>Never</span>}
      </td>
    );
  };

  const ActionsCellT = (props) => {
    const active = props.dataItem.status === 'Active';
    return (
      <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            onClick={() => toggleStatus(props.dataItem)}
            title={active ? 'Deactivate user' : 'Activate user'}
            style={{
              padding: '5px 10px', borderRadius: 8,
              border: `1px solid ${active ? 'rgba(217,119,6,0.25)' : 'rgba(5,150,105,0.25)'}`,
              background: active ? 'rgba(245,158,11,0.08)' : 'rgba(16,185,129,0.08)',
              color: active ? '#D97706' : '#059669',
              fontSize: 12, fontWeight: 700, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontFamily: 'inherit',
            }}
          >
            <Power size={12} strokeWidth={2.5} />
            {active ? 'Deactivate' : 'Activate'}
          </button>
                <button
                name="btnChangePassword"
            onClick={() => openPasswordChangeDialog(props.dataItem)}
            title="Change password"
            style={iconBtn('#7C3AED')}
          >
            <Key size={13} strokeWidth={2.5} />
          </button>
                <button
                    name="btnEdit"
            onClick={() => openEditDialog(props.dataItem)}
            title="Edit user"
            style={iconBtn('#0D9488')}
          >
            <Edit2 size={13} strokeWidth={2.5} />
          </button>
                <button
            name="btnDelete"
            onClick={() => handleDelete(props)}
            title="Delete user"
            style={iconBtn('#DC2626')}
          >
            <Trash2 size={13} strokeWidth={2.5} />
          </button>
        </div>
      </td>
    );
  };

  /*Grid */
    // Paging state
    const [page, setPage] = useState({
        skip: 0,
        take: 10,
    });

    // Sorting state
    const [sort, setSort] = useState([]);

    //  Process data (IMPORTANT)
    const processedData = useMemo(() => {
        return process(filteredUsers, {
            skip: page.skip,
            take: page.take,
            sort: sort,
        });
    }, [filteredUsers, page, sort]);

    const pagedUsers = useMemo(() => {
        return filteredUsers.slice(page.skip, page.skip + page.take);
    }, [filteredUsers, page]);

    // Page change handler
    //const handlePageChange = (event) => {
    //    setPage({
    //        skip: event.page.skip,
    //        take: event.page.take,
    //    });
    //};

    const handlePageChange = (event) => {
        const newPage = event.page || event;  //  fallback for CardsPagination

        setPage({
            skip: newPage.skip,
            take: newPage.take,
        });
    };

    // Sort change handler
    const handleSortChange = (event) => {
        setSort(event.sort);
    };

    const isUserFormValid = () => {
        return (
            validators.required(editingUser.firstName, "First Name") === null &&
            validators.alpha(editingUser.firstName) === null &&

            validators.required(editingUser.lastName, "Last Name") === null &&
            validators.alpha(editingUser.lastName) === null &&

            validators.required(editingUser.email, "Email") === null &&
            validators.email(editingUser.email) === null &&

            validators.required(editingUser.phone, "Phone") === null &&
            validators.phone(editingUser.phone) === null &&

            editingUser.role &&
            (editingUser.id ||
                (
                    validateUsername(editingUser.userName, users, editingUser.id).valid &&
                    validators.password(editingUser.password || "") === null &&
                    editingUser.password === editingUser.confirmPassword
                ))
        );
    };

    /*Grid */

  /* ── Render ────────────────────────────────────────────── */
  return (
      <div className="main-content">
          {pageLoading && (
              <div style={styles.overlay}>
                  <LoadingSpinner data-cy="page-loader-profile" message="Please wait..." fullPage="true" />
              </div>
          )}
      {/* ── Page header ───────────────────────────────── */}
      <div className="page-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <Users size={22} color="#0D9488" strokeWidth={2} />
            <h1 style={{ margin: 0 }}>User Management</h1>
          </div>
          <p>Manage portal users, roles, and access</p>
        </div>
        <button
          onClick={openAddDialog}
          style={{
            padding: '10px 18px', borderRadius: 12,
            border: 'none',
            background: 'linear-gradient(135deg, #0D9488, #0F766E)',
            color: '#fff', fontSize: 14, fontWeight: 700,
            cursor: 'pointer', display: 'inline-flex',
            alignItems: 'center', gap: 8, fontFamily: 'inherit',
            boxShadow: '0 4px 12px rgba(13,148,136,0.25)',
            transition: 'all 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
        >
          <UserPlus size={15} strokeWidth={2.5} />
          Add User
        </button>
      </div>

      {/* ── Stat cards ────────────────────────────────── */}
          <div className="stat-cards"
              /*style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 20 }}*/
              style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: '16px',
                  marginBottom: 20,
              }}
          >
              <div className="stat-card" style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
              }}>
          <div className="stat-icon teal"><Users size={20} strokeWidth={2} /></div>
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Total Users</div>
        </div>
              <div className="stat-card" style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
              }}>
          <div className="stat-icon green"><UserCheck size={20} strokeWidth={2} /></div>
          <div className="stat-value">{stats.active}</div>
          <div className="stat-label">Active</div>
        </div>
              <div className="stat-card" style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
              }}>
          <div className="stat-icon orange"><UserX size={20} strokeWidth={2} /></div>
          <div className="stat-value">{stats.inactive}</div>
          <div className="stat-label">Inactive</div>
        </div>
              <div className="stat-card" style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
              }}>
          <div className="stat-icon danger"><Shield size={20} strokeWidth={2} /></div>
          <div className="stat-value">{stats.admins}</div>
          <div className="stat-label">Admins</div>
        </div>
      </div>

      {/* ── Toolbar: filters + view toggle ─────────────── */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div
          className="user-mgmt-toolbar"
          style={{
            display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
          }}
        >
          <div style={{ position: 'relative', flex: '1 1 260px', minWidth: 200 }}>
            <span style={{
              position: 'absolute', left: 14, top: '50%',
              transform: 'translateY(-50%)', color: '#9CA3AF', display: 'flex',
            }}>
              <Search size={16} />
            </span>
            <input
              type="search"
              placeholder="Search by name, username/email, or contact email..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{
                width: '100%', padding: '10px 14px 10px 40px',
                borderRadius: 10, border: '1.5px solid #E8ECF0',
                fontSize: 14, fontFamily: 'inherit',
                background: '#F8FAFB', outline: 'none',
                transition: 'all 0.15s',
              }}
              onFocus={e => {
                e.target.style.borderColor = '#0D9488';
                e.target.style.background = '#fff';
              }}
              onBlur={e => {
                e.target.style.borderColor = '#E8ECF0';
                e.target.style.background = '#F8FAFB';
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Filter size={15} color="#9CA3AF" />
                      <select
                          name="allRolesSelect"
                          value={filterRole}
                          onChange={(e) => setFilterRole(e.target.value)}
                          style={selectStyle}
                        >
                        <option value="All">All Roles</option>
              
                          {roles.map((role) => (
                              <option key={role.Id } value={role.Name}>
                                  {role.Name}
                              </option>
                          ))}
            </select>
            <select
                name="allStatusesSelect"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={selectStyle}
            >
              <option value="All">All Statuses</option>
              <option value="Active">Active only</option>
              <option value="Inactive">Inactive only</option>
            </select>
          </div>

          {/* View toggle */}
          <div style={{
            display: 'flex', background: '#F1F5F9', borderRadius: 10,
            padding: 3, gap: 2,
          }}>
            <button
              onClick={() => setViewMode('cards')}
              style={toggleBtnStyle(viewMode === 'cards')}
              title="Card view"
              aria-pressed={viewMode === 'cards'}
            >
              <LayoutGrid size={14} strokeWidth={2.5} />
              Cards
            </button>
            <button
              onClick={() => setViewMode('table')}
              style={toggleBtnStyle(viewMode === 'table')}
              title="Table view"
              aria-pressed={viewMode === 'table'}
            >
              <List size={14} strokeWidth={2.5} />
              Table
            </button>
          </div>
        </div>

        {(searchText || filterRole !== 'All' || filterStatus !== 'All') && (
          <div style={{
            marginTop: 12, paddingTop: 12, borderTop: '1px solid #F1F5F9',
            fontSize: 13, color: '#6B7280',
            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          }}>
            <span>Showing <strong style={{ color: '#1A1D21' }}>{filteredUsers.length}</strong> of {users.length} users</span>
            <button
              onClick={() => { setSearchText(''); setFilterRole('All'); setFilterStatus('All'); }}
              style={{
                background: 'none', border: 'none',
                color: '#0D9488', fontWeight: 600, fontSize: 13,
                cursor: 'pointer', fontFamily: 'inherit', padding: 0,
              }}
            >
              Clear filters
            </button>
          </div>
        )}
      </div>

      {/* ── Body ──────────────────────────────────────── */}
      {loading && <LoadingSpinner message="Loading users..." />}
      {error && <ErrorCard message={error} onRetry={() => window.location.reload()} />}

      {!loading && !error && (
        filteredUsers.length === 0 ? (
          <EmptyState hasFilters={searchText || filterRole !== 'All' || filterStatus !== 'All'} />
              ) : viewMode === 'cards' ? (
            <>
          <div
            style={{
              display: 'grid',
              // minmax(280px, 1fr) keeps a single card inside a 316px phone
              // viewport while still giving multi-col layouts on larger screens.
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 16,
            }}
          >
            
                          {/*{processedData.map(u => (*/}
          {pagedUsers.map(u => (
              <UserCard
                key={u.id}
                user={u}
                onEdit={openEditDialog}
                //onDelete={handleDelete}
                  onDelete={() => handleDeleteCard(u)}
                //onToggleStatus={toggleStatus}
                    onToggleStatus={() => toggleStatus(u)}
                onPasswordChange={openPasswordChangeDialog}
              />
            ))}
                          
                          </div>
                          <CardsPagination skip={page.skip}
                              take={page.take}
                              total={filteredUsers.length}
                              onPageChange={handlePageChange}
                          />
               </>       

        ) : (
          <div style={{ overflow: 'hidden', borderRadius: 16 }}>
            <div className="grid-scroll-wrapper">
                                  <Grid
                                      style={{ height: "500px" }}
                                      data={processedData}         //MUST use processed data
                                      skip={page.skip}
                                      take={page.take}
                                      total={filteredUsers.length}        //  REQUIRED
                                      //pageable={true}
                                      pageable={{ pageSizes: [5, 10, 20] }}
                                      sortable={true}
                                      sort={sort}
                                      onPageChange={handlePageChange}
                                      onSortChange={handleSortChange}
                                  >
                <GridColumn field="firstName" title="User" width="280px" cells={{ data: NameCell }} />
                <GridColumn field="phone" title="Phone" width="150px" />
                <GridColumn field="role" title="Role" width="120px" cells={{ data: RoleCellT }} />
                <GridColumn field="status" title="Status" width="120px" cells={{ data: StatusCellT }} />
                <GridColumn field="lastLogin" title="Last Login" width="130px" cells={{ data: LastLoginCellT }} />
                <GridColumn title="Actions" width="220px" cells={{ data: ActionsCellT }} />
              </Grid>
            </div>
          </div>
        )
      )}

      {/* ── Dialog ────────────────────────────────────── */}
      {showDialog && editingUser && (
        <Dialog
          title={editingUser.id ? 'Edit User' : 'Add New User'}
          onClose={closeDialog}
          className="user-dialog"
          width={viewportWidth < 640 ? Math.max(viewportWidth - 24, 300) : 560}
        >
          <div style={{ padding: '8px 0' }}>
            <div className="form-grid">
              <div className="form-group">
                <label className="required">First Name</label>
                 <ValidatedInput
                                  //  type="text"
                  required
                                  value={editingUser.firstName}
                                  onChange={(v) => {
                                      updateField('firstName', v);
                                      const firstName = v;
                                      // Auto-suggest a username for NEW users only (id is null)
                                      // and only when the user hasn't manually typed one yet.
                                      if (!editingUser.id && !editingUser._usernameTouched) {
                                          const takenSet = new Set(
                                              users.map((u) => (u.userName || '').toLowerCase())
                                          );
                                          //const suggestion = suggestUsername(editingUser.firstName, editingUser.lastName, takenSet);
                                          const suggestion = suggestUsername(firstName, editingUser.lastName, takenSet);
                                          setEditingUser({ ...editingUser, firstName, userName: suggestion });
                                          //setEditingUser({ ...editingUser, username: suggestion });
                                      } else {
                                          setEditingUser({ ...editingUser, firstName });
                                      }
                                  }}
                  
                                  
                  validate={(v) => validators.required(v, 'First Name') || validators.alpha(v)}
                  placeholder="Enter first name"
                />
              </div>
              <div className="form-group">
                <label className="required">Last Name</label>
                              <ValidatedInput
                                  /// type="text"
                                  required
                  value={editingUser.lastName}
                                  onChange={(v) => {
                                      updateField('lastName', v);
                    //const lastName = e.target.value;
                    const lastName = v;
                                      if (!editingUser.id && !editingUser._usernameTouched) {
                                          
                      const takenSet = new Set(
                        users.map((u) => (u.userName || '').toLowerCase())
                      );
                        const suggestion = suggestUsername(editingUser.firstName, lastName, takenSet);
                        
                      setEditingUser({ ...editingUser, lastName, userName: suggestion });
                    } else {
                      setEditingUser({ ...editingUser, lastName });
                    }
                  }}
                                  placeholder="Enter last name"
                                  validate={(v) => validators.required(v, 'Last Name') || validators.alpha(v)}
                />
              </div>

              {/* Username with real-time validation */}
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="required">Username</label>
                <UsernameField
                  value={editingUser.userName || ''}
                  onChange={(v) => setEditingUser({
                    ...editingUser,
                    userName: v,
                    _usernameTouched: true,
                  })}
                  allUsers={users}
                  currentUserId={editingUser.id}
                  readOnly={!!editingUser.id /* usernames are immutable after creation */}
                />
              </div>

              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="required">Email</label>
                              <ValidatedInput
                                  required
                  type="email"
                                  value={editingUser.email}
                                  onChange={(v) => {
                                      updateField('email', v);
                                      setEditingUser({ ...editingUser, email: v });
                                  }}
                                  validate={(v) => validators.required(v, 'Email') || validators.email(v)}
                  //onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                                  placeholder="user@organization.com"
                                  readOnly={!!editingUser.id /* usernames are immutable after creation */}
                />
              </div>

              {/* Password fields — only when ADDING a new user. Existing
                  users change their password from the Profile page. */}
              {!editingUser.id && (
                <>
                  {/* Password-action row: spans both columns */}
                  <div style={{
                    gridColumn: '1 / -1',
                    display: 'flex',
                    gap: 8,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    marginBottom: -4,
                  }}>
                    <button
                      type="button"
                      onClick={async () => {
                        const pwd = generatePassword();
                        setEditingUser({ ...editingUser, password: pwd, confirmPassword: pwd });
                        setRevealPassword(true);
                        // Best-effort clipboard copy so the admin can share it securely.
                        try {
                          await navigator.clipboard?.writeText(pwd);
                          toast.success(
                            'Strong password generated and copied to clipboard. Share it with the user securely.',
                            { title: 'Password generated' }
                          );
                        } catch {
                          toast.info(
                            'Strong password generated. Copy it manually before saving — it will not be shown again.',
                            { title: 'Password generated' }
                          );
                        }
                      }}
                      style={{
                        padding: '8px 14px', borderRadius: 10,
                        border: '1px solid rgba(13,148,136,0.3)',
                        background: 'rgba(13,148,136,0.08)',
                        color: '#0D9488', fontSize: 13, fontWeight: 700,
                        cursor: 'pointer', fontFamily: 'inherit',
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                      }}
                      title="Generate a strong random password (12+ chars, mixed case, digit, symbol)"
                    >
                      <Key size={13} strokeWidth={2.5} />
                      Generate Password
                    </button>
                    <button
                      type="button"
                      onClick={() => setRevealPassword((r) => !r)}
                      style={{
                        padding: '8px 12px', borderRadius: 10,
                        border: '1px solid #E8ECF0',
                        background: '#fff',
                        color: '#6B7280', fontSize: 13, fontWeight: 600,
                        cursor: 'pointer', fontFamily: 'inherit',
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                      }}
                      title={revealPassword ? 'Hide password' : 'Show password'}
                    >
                      {revealPassword
                        ? <><EyeOff size={13} strokeWidth={2} /> Hide</>
                        : <><Eye size={13} strokeWidth={2} /> Show</>}
                    </button>
                    {editingUser.password && (
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await navigator.clipboard?.writeText(editingUser.password || '');
                            toast.success('Password copied to clipboard.', { title: 'Copied' });
                          } catch {
                            toast.error('Could not copy — clipboard access denied.', { title: 'Copy failed' });
                          }
                        }}
                        style={{
                          padding: '8px 12px', borderRadius: 10,
                          border: '1px solid #E8ECF0',
                          background: '#fff',
                          color: '#6B7280', fontSize: 13, fontWeight: 600,
                          cursor: 'pointer', fontFamily: 'inherit',
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                        }}
                        title="Copy current password to clipboard"
                      >
                        <Copy size={13} strokeWidth={2} /> Copy
                      </button>
                    )}
                  </div>

                  <ValidatedInput
                    label="New Password"
                    required
                    type={revealPassword ? 'text' : 'password'}
                    value={editingUser.password || ''}
                    onChange={(v) => setEditingUser({ ...editingUser, password: v })}
                    validate={(v) => validators.password(v)}
                    placeholder="Enter a strong password"
                    helpText="Min 8 chars, uppercase, lowercase, number"
                    autoComplete="new-password"
                  />
                  <ValidatedInput
                    label="Confirm Password"
                    required
                    type={revealPassword ? 'text' : 'password'}
                    value={editingUser.confirmPassword || ''}
                    onChange={(v) => setEditingUser({ ...editingUser, confirmPassword: v })}
                    validate={(v) =>
                      v && v !== (editingUser.password || '')
                        ? 'Passwords do not match'
                        : null
                    }
                    placeholder="Re-enter the password"
                    autoComplete="new-password"
                  />
                </>
              )}

              <div className="form-group">
                              <label className="required">Phone</label>
                              <ValidatedInput
                              required
                                  type="tel"
                                  onChange={(v) => {
                                      updateField('phone', v);
                                      setEditingUser({ ...editingUser, phone: v });
                                  }}
                                  validate={(v) => validators.required(v, 'Phone') || validators.phone(v)}
                  value={editingUser.phone}
                  //onChange={(e) => setEditingUser({ ...editingUser, phone: e.target.value })}
                                  placeholder="(512) 555-0000"
                                  format={formatters.phone}
                />
              </div>
              <div className="form-group">
                <label className="required">Role</label>
                <select
                  value={editingUser.role}
                  onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                >
                  {/*<option value="Super Admin">Super Admin</option>*/}
                  {/*<option value="Admin">Admin</option>*/}
                  {/*<option value="Provider">Provider</option>*/}
                  {/*<option value="Staff">Staff</option>*/}
                                  {/*<option value="Viewer">Viewer</option>*/}
                                  {roles.map((role) => (
                                      <option key={role.Id} value={role.Name}>
                                          {role.Name}
                                      </option>
                                  ))}
                </select>
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>Status</label>
                <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                  {['Active', 'Inactive'].map((s) => (
                    <label
                      key={s}
                      style={{
                        flex: 1, display: 'flex', alignItems: 'center', gap: 10,
                        padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
                        border: `1.5px solid ${editingUser.status === s ? '#0D9488' : '#E8ECF0'}`,
                        background: editingUser.status === s ? 'rgba(13,148,136,0.04)' : '#fff',
                        transition: 'all 0.15s',
                      }}
                    >
                      <input
                        type="radio"
                        name="status"
                        checked={editingUser.status === s}
                        onChange={() => setEditingUser({ ...editingUser, status: s })}
                        style={{ accentColor: '#0D9488' }}
                      />
                      <Circle size={9} strokeWidth={0} fill={s === 'Active' ? '#10B981' : '#9CA3AF'} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#1A1D21' }}>{s}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <DialogActionsBar>
            <Button onClick={closeDialog}>Cancel</Button>
            <Button
                          themeColor="primary"
              name="btnSave"
              onClick={handleSave}
              //disabled={
                //saving ||

             //   (!editingUser.id && !validateUsername(editingUser.userName, users, editingUser.id).valid) ||

               /// (!editingUser.id && (
               //   validators.password(editingUser.password || '') !== null ||
                 // (editingUser.password || '') !== (editingUser.confirmPassword || '')
              //  ))
                          //}
                          disabled={saving || !isUserFormValid()}
            >
              {saving ? 'Saving...' : editingUser.id ? 'Save Changes' : 'Add User'}
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}

      {/* ── Change Password Dialog (Admin reset) ─────── */}
      {passwordChangeUser && (
        <Dialog
          title={`Change Password — ${passwordChangeUser.firstName} ${passwordChangeUser.lastName}`}
          onClose={closePasswordChangeDialog}
          className="user-dialog"
          width={viewportWidth < 640 ? Math.max(viewportWidth - 24, 300) : 500}
        >
          <div style={{ padding: '4px 0' }}>
            {/* Info banner */}
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 12,
              padding: '12px 14px', borderRadius: 12,
              background: 'rgba(124,58,237,0.06)',
              border: '1px solid rgba(124,58,237,0.15)',
              marginBottom: 18,
            }}>
              <Key size={18} color="#7C3AED" strokeWidth={2.5} style={{ flexShrink: 0, marginTop: 2 }} />
              <div style={{ fontSize: 13, color: '#4B5563', lineHeight: 1.55 }}>
                You are resetting the password for{' '}
                <strong style={{ color: '#1A1D21' }}>
                  {passwordChangeUser.firstName} {passwordChangeUser.lastName}
                </strong>
                {passwordChangeUser.userName && (
                  <> (<code style={{
                    background: '#F1F5F9', padding: '1px 6px', borderRadius: 4,
                    fontSize: 12, fontFamily: 'monospace', color: '#7C3AED',
                  }}>{passwordChangeUser.userName}</code>)</>
                )}
                . Any active sessions for this user will be signed out.
                Share the new password with them through a secure channel.
              </div>
            </div>

            {/* Action row: Generate / Show / Copy */}
            <div style={{
              display: 'flex',
              gap: 8,
              flexWrap: 'wrap',
              alignItems: 'center',
              marginBottom: 12,
            }}>
              <button
                type="button"
                onClick={async () => {
                  const pwd = generatePassword();
                  setPwdNew(pwd);
                  setPwdConfirm(pwd);
                  setPwdReveal(true);
                  try {
                    await navigator.clipboard?.writeText(pwd);
                    toast.success(
                      'Strong password generated and copied to clipboard. Share it with the user securely.',
                      { title: 'Password generated' }
                    );
                  } catch {
                    toast.info(
                      'Strong password generated. Copy it manually before saving — it will not be shown again.',
                      { title: 'Password generated' }
                    );
                  }
                }}
                style={{
                  padding: '8px 14px', borderRadius: 10,
                  border: '1px solid rgba(124,58,237,0.3)',
                  background: 'rgba(124,58,237,0.08)',
                  color: '#7C3AED', fontSize: 13, fontWeight: 700,
                  cursor: 'pointer', fontFamily: 'inherit',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}
                title="Generate a strong random password"
              >
                <Key size={13} strokeWidth={2.5} />
                Generate Password
              </button>
              <button
                type="button"
                onClick={() => setPwdReveal((r) => !r)}
                style={{
                  padding: '8px 12px', borderRadius: 10,
                  border: '1px solid #E8ECF0',
                  background: '#fff',
                  color: '#6B7280', fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}
                title={pwdReveal ? 'Hide password' : 'Show password'}
              >
                {pwdReveal
                  ? <><EyeOff size={13} strokeWidth={2} /> Hide</>
                  : <><Eye size={13} strokeWidth={2} /> Show</>}
              </button>
              {pwdNew && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await navigator.clipboard?.writeText(pwdNew);
                      toast.success('Password copied to clipboard.', { title: 'Copied' });
                    } catch {
                      toast.error('Could not copy — clipboard access denied.', { title: 'Copy failed' });
                    }
                  }}
                  style={{
                    padding: '8px 12px', borderRadius: 10,
                    border: '1px solid #E8ECF0',
                    background: '#fff',
                    color: '#6B7280', fontSize: 13, fontWeight: 600,
                    cursor: 'pointer', fontFamily: 'inherit',
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                  }}
                  title="Copy current password to clipboard"
                >
                  <Copy size={13} strokeWidth={2} /> Copy
                </button>
              )}
            </div>

            <div className="form-grid">
              <ValidatedInput
                label="New Password"
                required
                type={pwdReveal ? 'text' : 'password'}
                value={pwdNew}
                onChange={setPwdNew}
                            //  validate={(v) => validators.password(v)}
                              validate={(v) => validators.required(v, 'Password') || validators.password(v)}
                placeholder="Enter a strong password"
                helpText="Min 8 chars, uppercase, lowercase, number"
                autoComplete="new-password"
              />
              <ValidatedInput
                label="Confirm Password"
                required
                type={pwdReveal ? 'text' : 'password'}
                value={pwdConfirm}
                onChange={setPwdConfirm}
                validate={(v) =>
                  v && v !== pwdNew ? 'Passwords do not match' : null
                }
                placeholder="Re-enter the password"
                autoComplete="new-password"
              />
            </div>
          </div>
          <DialogActionsBar>
            <Button onClick={closePasswordChangeDialog}>Cancel</Button>
                      <Button
              name="btnSavePasswordChange"
              themeColor="primary"
              onClick={handleSavePasswordChange}
              disabled={
                pwdSaving ||
                validators.password(pwdNew) !== null ||
                  pwdNew !== pwdConfirm ||
                  validators.required(pwdNew, 'Password')
              }
            >
              {pwdSaving ? 'Saving...' : 'Change Password'}
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  );
};

/* ── Empty state ────────────────────────────────────────── */
const EmptyState = ({ hasFilters }) => (
  <div className="card" style={{ textAlign: 'center', padding: '56px 24px' }}>
    <div style={{
      width: 64, height: 64, borderRadius: 16,
      background: 'rgba(13,148,136,0.08)',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      marginBottom: 16,
    }}>
      {hasFilters
        ? <Search size={28} color="#0D9488" strokeWidth={2} />
        : <Users size={28} color="#0D9488" strokeWidth={2} />
      }
    </div>
    <div style={{ fontSize: 17, fontWeight: 700, color: '#1A1D21', marginBottom: 6 }}>
      {hasFilters ? 'No users match your filters' : 'No users yet'}
    </div>
    <div style={{ fontSize: 13, color: '#6B7280' }}>
      {hasFilters ? 'Try clearing the filters or adjusting the search.' : 'Add your first portal user to get started.'}
    </div>
  </div>
);

/* ── Inline style helpers ──────────────────────────────── */
const iconBtn = (color) => ({
  width: 30, height: 30, borderRadius: 8,
  border: `1px solid ${color}33`,
  background: `${color}0A`,
  color,
  cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  padding: 0,
});

const selectStyle = {
  padding: '8px 12px', borderRadius: 10,
  border: '1.5px solid #E8ECF0',
  fontSize: 13, fontWeight: 600, color: '#374151',
  background: '#F8FAFB', cursor: 'pointer',
  fontFamily: 'inherit', outline: 'none',
};

const toggleBtnStyle = (active) => ({
  padding: '7px 12px', borderRadius: 8, border: 'none',
  background: active ? '#fff' : 'transparent',
  color: active ? '#0D9488' : '#6B7280',
  fontSize: 12, fontWeight: 700,
  cursor: 'pointer', display: 'inline-flex',
  alignItems: 'center', gap: 5,
  boxShadow: active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
  fontFamily: 'inherit',
  transition: 'all 0.15s',
});

export default UserManagementPage;
